import React, { useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Field } from 'react-final-form';
import './AuthLogin.css';
import { LoginNavbar } from '../../components/layout/Navbar';
import { loginStart, loginSuccess, loginFailure } from '../../store/slices/authSlice';
import { login } from '../../utils/api';
import '../../components/layout/LayoutComponents.css';

const Footer = ({ className }) => (
  <footer className={className || 'footer'}>
    @ EZ Timesheet Copyright Reserved
  </footer>
);

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error } = useSelector((state) => state.auth);

  // Lock scroll and page height only while on login
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.height = '100vh';

    return () => {
      document.body.style.overflow = '';
      document.body.style.height = '';
    };
  }, []);

  const onSubmit = async (values) => {
    try {
      dispatch(loginStart());

      // Backend may accept either email or username. Try email first then fallback.
      let res;
      try {
        res = await login({ email: values.email, password: values.password });
      } catch (err) {
        console.error('Login error:', err?.response?.data || err?.message);
        throw err;
        //const status = err?.response?.status;
        // Some backends respond 400/401/403 for auth failures.
        //if (status === 400 || status === 401 || status === 403) {
        //  res = await login({ username: values.email, password: values.password });
        //} else {
        //  throw err;
        //}
      }

      const data = res?.data?.data || res?.data;
      const token = data?.token || data?.accessToken || data?.jwt;
      let user = data?.user || data?.userDto || data?.profile || null;

      // Some backends return { token, ...userFields } without nesting under `user`.
      if (!user && data && typeof data === 'object') {
        const { token: _t, accessToken: _at, jwt: _jwt, ...rest } = data;
        if (Object.keys(rest).length > 0) user = rest;
      }

      if (!token) throw new Error('Login succeeded but no token was returned by the API');
      if (!user) throw new Error('Login succeeded but no user profile was returned by the API');
      
      // Employee Role Check
      const isEmployee = user?.roles?.some(r => r.roleName === "EMPLOYEE");
      if(!isEmployee) throw new Error('Not an Employee');

      localStorage.setItem('token', token);
      localStorage.setItem('auth', JSON.stringify({ token, user }));
      
      dispatch(loginSuccess({ token, user }));

      // redirect after login
      const from = location.state?.from?.pathname || '/dashboard';
       navigate(from, { replace: true });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Login error:', err?.response?.data || err?.message);

      const status = err?.response?.status;
      let message;

      // For typical auth failures, always show a friendly message
      if (status === 401) {
        message = 'Invalid email or password';
      } else {
        message =
          err?.response?.data?.message ||
          err?.message ||
          'Login failed. Please try again.';
      }

      dispatch(loginFailure(message));
    }
  };

  const validate = (values) => {
    const errors = {};

    if (!values.email) {
      errors.email = 'Email or username is required';
    } else if (String(values.email).includes('@') && !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(values.email)) {
      errors.email = 'Invalid email address';
    }

    if (!values.password) {
      errors.password = 'Password is required';
    } else if (
      !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,16}$/.test(values.password)
    ) {
      errors.password =
        'Password must be 8-16 characters, include upper and lower case letters, a number, and a special character';
    }

    return errors;
  };

  return (
    <>
      <LoginNavbar />
      <div className="auth-login-container">
        <div className="auth-login-card">
          <h1 className="auth-login-title">Login</h1>
          <Form
            onSubmit={onSubmit}
            validate={validate}
            render={({ handleSubmit, submitting }) => (
              <form onSubmit={handleSubmit}>
                <div className="auth-form-group auth-form-row">
                  <label className="auth-label" htmlFor="login-email">Email or username</label>
                  <Field name="email">
                    {({ input, meta }) => (
                      <>
                        <input {...input} id="login-email" type="text" placeholder="Enter your email or username" className="auth-input" required />
                        {meta.touched && meta.error && (
                          <div className="auth-error-message">{meta.error}</div>
                        )}
                      </>
                    )}
                  </Field>
                </div>

                <div className="auth-form-group auth-form-row">
                  <label className="auth-label" htmlFor="login-password">Password</label>
                  <Field name="password">
                    {({ input, meta }) => (
                      <>
                        <input {...input} id="login-password" type="password" placeholder="Enter your password" className="auth-input" required />
                        {meta.touched && meta.error && (
                          <div className="auth-error-message">{meta.error}</div>
                        )}
                      </>
                    )}
                  </Field>
                </div>

                <div className="auth-checkbox-row">
                  <div className="auth-checkbox-group">
                    <Field name="rememberMe" type="checkbox">
                      {({ input }) => (
                        <input {...input} type="checkbox" />
                      )}
                    </Field>
                    <label className="auth-label">Remember me</label>
                  </div>
                  <Link to="/forgot-password" className="auth-forgot-password-inline">
                    Forgot password?
                  </Link>
                </div>

                {error && <div className="auth-error-message">{error}</div>}

                <button type="submit" disabled={loading || submitting} className="auth-submit-button">
                  {loading ? 'Logging in...' : 'Login'}
                </button>

                <div className="auth-legal-links">
                  <Link to="/terms">
                    Terms & Conditions
                  </Link>
                  <span style={{ margin: '0 8px' }}></span>
                  <Link to="/privacy">
                    Privacy Policy
                  </Link>
                </div>
              </form>
            )}
          />
        </div>
      </div>
      <Footer className="login-footer" />
    </>
  );
};

export default Login;
